import { forwardRef, type ComponentRef } from "react";

import { Divider, Typography } from "../../atoms";
import { Icon } from "../../atoms/icons";
import { cn, platformProps } from "../../lib";
import { Box, TabsListPrimitive, TabsRootPrimitive, TabsTriggerPrimitive } from "../../primitives";
import { iconSize } from "../../tokens";
import { getTabItemState, tabHoverColor, tabIconColor, tabTypographyColor } from "./lib";
import { tabItemVariants } from "./tab.variants";
import type { TabProps } from "./types";

export const Tab = forwardRef<ComponentRef<typeof TabsRootPrimitive>, TabProps>(
  ({ items, active, onChange, fullWidth = false, className, testID }, ref) => {
    return (
      <TabsRootPrimitive
        ref={ref}
        value={active}
        // A press on the selected tab isn't a change: @rn-primitives/tabs
        // reports every press on iOS and Android, where Radix on web doesn't
        onValueChange={(value) => {
          if (value !== active) onChange(value);
        }}
        // Layout classes go on the root, so they move the divider with the tabs
        className={className}
        {...platformProps({ testID, slot: "tab" })}
      >
        <Box className="flex flex-col">
          <TabsListPrimitive className="flex flex-row items-end justify-start">
            {items.map((item) => {
              const isActive = active === item.value;
              const disabled = item.disabled ?? false;
              const state = getTabItemState(isActive, disabled);
              return (
                <TabsTriggerPrimitive
                  key={item.value}
                  value={item.value}
                  disabled={disabled}
                  // Tab renders no panels, so the trigger's default aria-controls
                  // would point at nothing (invalid ARIA, ADR-025)
                  aria-controls={undefined}
                  className={tabItemVariants({ fullWidth, active: isActive })}
                >
                  <Box className="gap-spacing-xxs py-spacing-md relative flex min-w-0 max-w-full flex-col items-center justify-center">
                    {item.icon && (
                      <Icon
                        name={item.icon}
                        size={iconSize.sm}
                        className={cn(tabIconColor[state], tabHoverColor[state])}
                      />
                    )}
                    {/* One line ending in "…": `truncate` on web, `numberOfLines` on native */}
                    <Typography
                      variant="label-sm"
                      color={tabTypographyColor[state]}
                      numberOfLines={1}
                      className={cn("max-w-full truncate", tabHoverColor[state])}
                    >
                      {item.label}
                    </Typography>
                    {isActive && (
                      <Box
                        className={cn(
                          "rounded-t-radius-full h-tab-indicator absolute bottom-0 left-0 right-0",
                          !disabled
                            ? "bg-luce-brand-primary"
                            : "bg-luce-neutral-foreground-attenuated",
                        )}
                        aria-hidden
                      />
                    )}
                  </Box>
                </TabsTriggerPrimitive>
              );
            })}
          </TabsListPrimitive>
          <Divider />
        </Box>
      </TabsRootPrimitive>
    );
  },
);

Tab.displayName = "Tab";
